// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-template-loop-header' )
export class AutomatorTemplateItemLoopHeader extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * Information about the data to be iterated.
	 * Can represent users, posts, or tokens with relevant metadata.
	 *
	 * @type {Object}
	 * @property {Object} iterableExpression - Contains details about the data being iterated.
	 * Attribute name in HTML: none (non-reflected property).
	 */
	@property( { type: Object, attribute: false } )
	accessor iterableExpression;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="header">
				<div class="header-tag">
					<div class="header-tag-icon">
						<uo-icon id="repeat"></uo-icon>
					</div>

					${ escapeHTML( __( 'Loop', 'uncanny-automator' ) ) }
				</div>

				<div class="header-logic">
					${ this._templateLogicExpression }
					${ escapeHTML( __( 'that match the following criteria', 'uncanny-automator' ) ) }
				</div>
			</div>
		`;
	}

	/**
	 * Creates the block that shows what type of data we're iterating. E.g. "Users"
	 *
	 * @return {TemplateResult|undefined} The expression/source data
	 */
	get _templateLogicExpression() {
		if ( this.iterableExpression.type === 'users' ) {
			return html`
				<div class="header-logic-expression">
					<uo-icon id="user"></uo-icon>
					${ escapeHTML( __( 'Users', 'uncanny-automator' ) ) }
				</div>
			`;
		}

		if ( this.iterableExpression.type === 'posts' ) {
			return html`
				<div class="header-logic-expression">
					<uo-icon id="files"></uo-icon>
					${ escapeHTML( __( 'Posts', 'uncanny-automator' ) ) }
				</div>
			`;
		}

		if ( this.iterableExpression.type === 'token' ) {
			return html`
				<div class="header-logic-expression header-logic-expression--token">
					${ this._templateIterableExpressionToken }
				</div>
			`;
		}
	}

	/**
	 * If this is a Token Loop, returns the token expression
	 *
	 * @return {TemplateResult|undefined} The token
	 */
	get _templateIterableExpressionToken() {
		// Check if the iterable expression is a token
		if ( this.iterableExpression.type !== 'token' ) {
			return;
		}

		if ( isEmpty( this.iterableExpression.title_html ) ) {
			return html`Title missing`;
		}

		// Return the sentence
		return html`
			<uap-template-sentence .sentence=${ this.iterableExpression.title_html }></uap-template-sentence>
		`;
	}
}